// SessionFramingStep — Pre-session context for adaptive teaching
// Collects student's level and pace before voice session starts.
// Data is passed to useAnnaChat session context for adaptive teaching.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import type { SessionFraming, TeachingForm } from '../../types';

interface SessionFramingStepProps {
  onSubmit: (framing: SessionFraming) => void;
  onSkip: () => void;
  teachingForm?: TeachingForm;
}

const SessionFramingStep: React.FC<SessionFramingStepProps> = ({ onSubmit, onSkip, teachingForm }) => {
  const { t } = useTranslation('session');

  const [level, setLevel] = useState<SessionFraming['level']>('intermediate');
  const [pace, setPace] = useState<SessionFraming['pace']>('normal');
  const [examStartPoint, setExamStartPoint] = useState<SessionFraming['examStartPoint']>('recognition');

  const handleSubmit = () => {
    onSubmit({
      level,
      pace,
      ...(teachingForm === 'exam' && { examStartPoint }),
    });
  };

  const levelOptions: { value: SessionFraming['level']; label: string }[] = [
    { value: 'novice', label: t('sessionFraming.level.novice') },
    { value: 'intermediate', label: t('sessionFraming.level.intermediate') },
    { value: 'advanced', label: t('sessionFraming.level.advanced') },
  ];

  const paceOptions: { value: NonNullable<SessionFraming['pace']>; label: string }[] = [
    { value: 'slow', label: t('sessionFraming.pace.slow') },
    { value: 'normal', label: t('sessionFraming.pace.normal') },
    { value: 'fast', label: t('sessionFraming.pace.fast') },
  ];

  const examStartPointOptions: { value: NonNullable<SessionFraming['examStartPoint']>; label: string }[] = [
    { value: 'recognition', label: t('sessionFraming.examStartPoint.recognition') },
    { value: 'formulation', label: t('sessionFraming.examStartPoint.formulation') },
    { value: 'freePresentation', label: t('sessionFraming.examStartPoint.freePresentation') },
  ];

  return (
    <div className="max-w-md mx-auto px-4 py-8">
      <div className="bg-white dark:bg-neutral-dark rounded-2xl shadow-soft p-6">
        <h2 className="text-xl font-serif font-semibold text-neutral-dark dark:text-white mb-1">
          {t('sessionFraming.title')}
        </h2>
        <p className="text-sm text-neutral-medium mb-6">
          {t('sessionFraming.subtitle')}
        </p>

        {/* Level */}
        <fieldset className="mb-5">
          <legend className="text-sm font-medium text-neutral-dark dark:text-white mb-2">
            {t('sessionFraming.level.label')}
          </legend>
          <div className="flex flex-col gap-2">
            {levelOptions.map(opt => (
              <label
                key={opt.value}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                  level === opt.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-border-light dark:border-border-dark hover:border-primary-300'
                }`}
              >
                <input
                  type="radio"
                  name="level"
                  value={opt.value}
                  checked={level === opt.value}
                  onChange={() => setLevel(opt.value)}
                  className="sr-only"
                />
                <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  level === opt.value ? 'border-primary-500' : 'border-neutral-medium'
                }`}>
                  {level === opt.value && <span className="w-2 h-2 rounded-full bg-primary-500" />}
                </span>
                <span className="text-sm text-neutral-dark dark:text-white">{opt.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {/* Pace */}
        <fieldset className="mb-6">
          <legend className="text-sm font-medium text-neutral-dark dark:text-white mb-2">
            {t('sessionFraming.pace.label')}
          </legend>
          <div className="flex gap-2">
            {paceOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPace(opt.value)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pace === opt.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-neutral-dark dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Exam start point */}
        {teachingForm === 'exam' && (
          <fieldset className="mb-6">
            <legend className="text-sm font-medium text-neutral-dark dark:text-white mb-2">
              {t('sessionFraming.examStartPoint.label')}
            </legend>
            <div className="flex flex-col gap-2">
              {examStartPointOptions.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                    examStartPoint === opt.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-border-light dark:border-border-dark hover:border-primary-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="examStartPoint"
                    value={opt.value}
                    checked={examStartPoint === opt.value}
                    onChange={() => setExamStartPoint(opt.value)}
                    className="sr-only"
                  />
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    examStartPoint === opt.value ? 'border-primary-500' : 'border-neutral-medium'
                  }`}>
                    {examStartPoint === opt.value && <span className="w-2 h-2 rounded-full bg-primary-500" />}
                  </span>
                  <span className="text-sm text-neutral-dark dark:text-white">{opt.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onSkip} className="flex-1">
            {t('sessionFraming.skip')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} className="flex-1">
            {t('sessionFraming.startSession')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionFramingStep;
